<template>
  <v-stepper-content :step="step">
    <div class="text-h5 font-weight-medium">{{ $t('BasicInfo') }}</div>
    <v-row class="align-center">
      <v-col cols="12" class="d-flex no-gutters" sm="auto">
        <MenuGroup>
          <DvrTimePicker
            :label="$t('CheckIn')"
            border-radius="left"
            :min="minCheckin"
            :value.sync="checkInTime"
          />
          <DvrTimePicker
            :label="$t('CheckOut')"
            border-radius="right"
            :max="maxCheckout"
            :value.sync="checkOutTime"
          />
        </MenuGroup>
      </v-col>
      <v-col cols="12" sm="auto">
        <GuestsSelect
          :value.sync="guests"
          :readonly="hasChekinVerification || lockGuestDetails"
          :disable-guest-count-changes="checkinInfo.disable_guest_count_changes"
        />
      </v-col>
    </v-row>
    <div class="text-h5 font-weight-medium my-4">{{ $t('Guest Details') }}</div>
    <div v-if="hasChekinVerification">
      <span v-if="chekinCompleteError" class="error--text">
        {{ chekinCompleteError }}
      </span>
      <chekin-guest-sdk
        :api-key="chekinGuestKey"
        :reservation-id="checkInVerificationIdentifier"
        :step="step"
        :pre-check-in="preCheckIn"
      />
    </div>
    <v-form v-else ref="form">
      <v-row v-for="(guest, ind) in guestsDetails" :key="'guest' + ind">
        <v-col cols="12">
          <div class="font-weight-medium">
            Guest #{{ ind + 1 }}
            <span v-if="ind < adultsCount" class="text-caption">
              ({{ $t('Adult') }})
            </span>
            <span v-else class="text-caption"> ({{ $t('Child') }}) </span>
          </div>
        </v-col>

        <v-col cols="12" sm="3" class="py-1">
          <v-combobox
            v-model="guestsDetails[ind].full_name"
            :disabled="lockGuestDetails"
            :items="previousGuests"
            item-text="full_name"
            :rules="checkinInfo.optional_guest_names ? [] : [required]"
            outlined
            dense
            :label="$t('Full Name')"
            clearable
            @change="selected => applyPreviousGuestDetails(selected, ind)"
            return-object
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.email"
                  >Email: {{ item.email }}</v-list-item-subtitle
                >
                <v-list-item-subtitle v-if="item.phone_number"
                  >Phone: {{ item.phone_number }}</v-list-item-subtitle
                >
              </v-list-item-content>
            </template>
          </v-combobox>
        </v-col>
        <v-col v-if="guestPassport" cols="12" sm="3" class="py-1">
          <v-text-field
            v-model="guestsDetails[ind].passport"
            :disabled="lockGuestDetails"
            :rules="
              ind < adultsCount && !guestIdDocsDeniedForChannel
                ? [required]
                : []
            "
            outlined
            dense
            :label="$t('Passport Number')"
          />
        </v-col>
        <v-col v-if="guestId" cols="12" sm="3" class="py-1">
          <v-text-field
            v-model="guestsDetails[ind].id"
            :disabled="lockGuestDetails"
            :rules="
              ind < adultsCount && !guestIdDocsDeniedForChannel
                ? [required]
                : []
            "
            outlined
            dense
            :label="$t('ID Number')"
          />
        </v-col>
        <v-col v-if="guestPhone" cols="12" sm="3" class="py-1">
          <v-text-field
            v-model.number="guestsDetails[ind].phone_number"
            :disabled="lockGuestDetails"
            :rules="ind < adultsCount ? [required] : []"
            outlined
            dense
            type="number"
            :label="$t('Phone Number')"
          />
        </v-col>
        <v-col v-if="guestEmail" cols="12" sm="3" class="py-1">
          <v-text-field
            v-model="guestsDetails[ind].email"
            :disabled="lockGuestDetails"
            :rules="ind < adultsCount ? [required] : []"
            outlined
            dense
            :label="$t('Email')"
          />
        </v-col>
        <v-col v-if="guestAddress" cols="12" sm="6" class="py-1">
          <v-text-field
            v-model="guestsDetails[ind].address"
            :disabled="lockGuestDetails"
            :rules="ind < adultsCount ? [required] : []"
            outlined
            dense
            :label="$t('Address')"
          />
        </v-col>
        <v-col v-if="guestPhotoId" cols="12" sm="12" class="py-1">
          <media-upload
            :value.sync="guestsDetails[ind].photo_id"
            :multiple="false"
            :disabled="lockGuestDetails"
            :required="ind < adultsCount && !guestIdDocsDeniedForChannel"
            :camera="true"
            :label="$t('Photo ID')"
            :preview="!!guestsDetails[ind].photo_id"
            :error-message="
              attemptedValidation &&
              ind < adultsCount &&
              !guestIdDocsDeniedForChannel &&
              !guestsDetails[ind].photo_id
                ? $t('Photo ID is required')
                : ''
            "
          />
        </v-col>
      </v-row>
    </v-form>
    <v-row class="purpose-section">
      <v-col>
        <div class="mb-2">{{ $t('What is the purpose of your visit?') }}</div>
        <v-radio-group
          v-model="clientInfo.trip_purpose"
          class="font-weight-medium mt-0 pt-0"
          style="margin-right: -15px"
          hide-details
          row
          @change="showTripPurposeError = false"
        >
          <v-radio :label="$t('Leisure')" value="leisure" />
          <v-radio :label="$t('Business')" value="business" />
        </v-radio-group>
        <div v-if="showTripPurposeError" class="error--text text-caption mt-1">
          {{ $t('Please select the purpose of your visit') }}
        </div>
      </v-col>
    </v-row>
  </v-stepper-content>
</template>

<script>
import MenuGroup from '@/components/common/menu-group'
import GuestsSelect from '@/components/common/guests-select'
import formRules from '@/components/mixins/form-rules-mixin'
import DvrTimePicker from '@/components/common/dvr-time-picker'
import ChekinGuestSdk from '@/components/common/chekin-guest-sdk.vue'
import MediaUpload from '@/components/common/media-upload'
import { isEmpty } from 'lodash/fp'
import CommonFunctions from '@/components/mixins/common-functions'

export default {
  name: 'GuestsInfo',
  components: {
    DvrTimePicker,
    GuestsSelect,
    MenuGroup,
    ChekinGuestSdk,
    MediaUpload,
  },
  mixins: [formRules, CommonFunctions],
  props: ['preCheckIn', 'step'],
  data() {
    return {
      minCheckin: this.preCheckIn.min_check_in_time_string,
      maxCheckout: this.preCheckIn.max_check_out_time_string,
      clientInfo: this.preCheckIn.client_info,
      guests: {
        adults: +(this.preCheckIn.guest_count_info.adults || 1),
        kids: +(this.preCheckIn.guest_count_info.kids || 0),
        babies: +(this.preCheckIn.guest_count_info.babies || 0),
      },
      guestNames: this.preCheckIn.guest_count_info.guests,
      guestsDetails: [],
      checkInTime: this.preCheckIn.times_info.requested_check_in_time,
      checkOutTime: this.preCheckIn.times_info.requested_check_out_time,
      checkinInfo: this.preCheckIn.checkin_info || {},
      chekinCompleteError: null,
      attemptedValidation: false,
      showTripPurposeError: false,
    }
  },
  computed: {
    guestIdDocsDeniedForChannel() {
      return this.isGuestIdDocsDeniedForChannel(this.checkinInfo)
    },
    hasChekinVerification() {
      return (
        !!this.chekinGuestKey && !isEmpty(this.checkInVerificationIdentifier)
      )
    },
    checkInVerificationIdentifier() {
      return this.preCheckIn.chekin_verification_identifier
    },
    allGuestDetails() {
      return this.checkinInfo.require_all_guest_details
    },
    lockGuestDetails() {
      return this.preCheckIn.locked_guest_details
    },
    guestPassport() {
      return (
        this.allGuestDetails &&
        this.checkinInfo.require_guest_passport &&
        !this.guestIdDocsDeniedForChannel
      )
    },
    guestId() {
      return (
        this.allGuestDetails &&
        this.checkinInfo.require_guest_id &&
        !this.guestIdDocsDeniedForChannel
      )
    },
    guestAddress() {
      return this.allGuestDetails && this.checkinInfo.require_guest_address
    },
    guestEmail() {
      return this.allGuestDetails && this.checkinInfo.require_guest_email
    },
    guestPhone() {
      return this.allGuestDetails && this.checkinInfo.require_guest_phone
    },
    previousGuests() {
      return this.preCheckIn?.previous_check_in_info?.guests_details || []
    },
    guestPhotoId() {
      return (
        this.allGuestDetails &&
        this.checkinInfo.require_guest_photo_id &&
        !this.guestIdDocsDeniedForChannel
      )
    },
    adultsCount() {
      return +this.guests.adults
    },
  },
  watch: {
    guests: {
      handler() {
        this.initializeGuestDetails()
      },
      deep: true,
    },
  },
  mounted() {
    if (this.preCheckIn.guests_details.length) {
      this.guestsDetails = this.preCheckIn.guests_details
    } else {
      this.initializeGuestDetails()
    }
    window.addEventListener(
      'chekin-all-guests-registered',
      this.onChekinAllGuestsRegistered
    )
  },
  beforeDestroy() {
    window.removeEventListener(
      'chekin-all-guests-registered',
      this.onChekinAllGuestsRegistered
    )
  },
  methods: {
    initializeGuestDetails() {
      const newCount = this.checkinInfo.primary_guest_info_only
        ? 1
        : this.guests.adults + this.guests.kids + this.guests.babies
      const countChange = newCount - this.guestsDetails.length

      if (countChange > 0) {
        this.guestsDetails.push(
          ...new Array(countChange).fill(null).map(() => ({}))
        )
      } else if (countChange < 0) {
        this.guestsDetails.splice(newCount, Math.abs(countChange))
      }

      const firstGuest = this.guestsDetails[0] || {}
      if (!firstGuest.full_name) firstGuest.full_name = this.guestNames[0]
      if (!firstGuest.phone_number) {
        firstGuest.phone_number = Number(this.clientInfo.phone_number)
      }
    },
    async validate() {
      this.attemptedValidation = true

      // Trip purpose is required (no default) — block advancing until chosen.
      this.showTripPurposeError = !this.clientInfo.trip_purpose
      if (this.showTripPurposeError) return

      if (this.hasChekinVerification) {
        this.chekinCompleteError = null

        const data = await this.$store.dispatch(
          'getChekinReservationStatus',
          this.preCheckIn.reservation_id
        )

        if (
          data.payload.data.verification_details.guests_registration_status !==
          'COMPLETE'
        ) {
          this.chekinCompleteError = 'Please complete the check-in process'
          return
        }

        const payload = {
          times_info: {
            requested_check_in_time: this.checkInTime,
            requested_check_out_time: this.checkOutTime,
          },
          client_info: this.clientInfo,
        }
        this.$emit('step-forward', payload)
      } else {
        const formValid = this.$refs.form.validate()
        const photoIdsValid =
          !this.guestPhotoId ||
          this.guestIdDocsDeniedForChannel ||
          this.guestsDetails.every(
            (guest, index) => index >= this.adultsCount || guest.photo_id
          )

        if (formValid && photoIdsValid) {
          const payload = {
            times_info: {
              requested_check_in_time: this.checkInTime,
              requested_check_out_time: this.checkOutTime,
            },
            guest_count_info: { ...this.guests, guests: this.guestNames },
            client_info: this.clientInfo,
            guests_details: this.guestsDetails,
          }
          this.$emit('step-forward', payload)
        }
      }
    },
    onChekinAllGuestsRegistered() {
      const payload = {
        times_info: {
          requested_check_in_time: this.checkInTime,
          requested_check_out_time: this.checkOutTime,
        },
        client_info: this.clientInfo,
      }
      this.$emit('step-forward', payload)
    },
    applyPreviousGuestDetails(selected, index) {
      if (typeof selected === 'string') {
        this.guestsDetails[index] = {
          ...this.guestsDetails[index],
          full_name: selected,
        }
      } else if (selected) {
        this.guestsDetails = [
          ...this.guestsDetails.slice(0, index),
          { ...selected },
          ...this.guestsDetails.slice(index + 1),
        ]
      }
    },
  },
}
</script>

<style scoped>
.purpose-section {
  position: relative;
  z-index: 1;
}
</style>
